/**
 * Google Analytics 4 (GA4) Integration for WeDo HR Services
 * 
 * This utility handles:
 * - Automatic page view tracking
 * - Custom event tracking for key user actions
 * 
 * Setup Instructions:
 * 1. Create a Google Analytics 4 property at https://analytics.google.com
 * 2. Get your Measurement ID (starts with "G-")
 * 3. Add it to your frontend .env file as REACT_APP_GA_MEASUREMENT_ID
 * 4. The integration will automatically activate when the ID is present
 */

// Get Measurement ID from environment
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

// Check if GA is available and initialized
const isGAAvailable = () => {
  return typeof window !== 'undefined' && 
         window.gtag && 
         GA_MEASUREMENT_ID;
};

/**
 * Initialize Google Analytics
 * Called once when the app loads
 */
export const initGA = () => {
  if (!GA_MEASUREMENT_ID) {
    console.log('[Analytics] No GA Measurement ID configured. Tracking disabled.');
    return;
  }

  // Check if already initialized
  if (window.gtag) {
    console.log('[Analytics] GA4 already initialized');
    return;
  }

  // Add GA4 script dynamically
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: false // We'll handle page views manually for SPA
  });

  console.log('[Analytics] GA4 initialized with ID:', GA_MEASUREMENT_ID);
};

/**
 * Track page views
 * Call this when navigating to a new page/route
 */
export const trackPageView = (path, title) => {
  if (!isGAAvailable()) return;

  window.gtag('event', 'page_view', {
    page_path: path || window.location.pathname,
    page_title: title || document.title,
    page_location: window.location.href
  });
};

/**
 * Track custom events
 * @param {string} eventName - Name of the event (e.g., 'job_application_submitted')
 * @param {object} params - Additional parameters for the event
 */
export const trackEvent = (eventName, params = {}) => {
  if (!isGAAvailable()) {
    // Log to console in development when GA not available
    if (process.env.NODE_ENV === 'development') {
      console.log('[Analytics Event]', eventName, params);
    }
    return;
  }

  window.gtag('event', eventName, params);
};

// ==================== PRE-DEFINED EVENT TRACKING FUNCTIONS ====================

/**
 * Track job application submission
 */
export const trackJobApplication = (jobId, jobTitle) => {
  trackEvent('job_application_submitted', {
    event_category: 'recruitment',
    job_id: jobId,
    job_title: jobTitle
  });
};

/**
 * Track talent pool signup
 */
export const trackTalentPoolSignup = () => {
  trackEvent('talent_pool_signup', {
    event_category: 'recruitment'
  });
};

/**
 * Track training enrollment request
 */
export const trackTrainingEnrollment = (courseId, courseTitle) => {
  trackEvent('training_enrollment_requested', {
    event_category: 'training',
    course_id: courseId,
    course_title: courseTitle
  });
};

/**
 * Track contact form submission
 */
export const trackContactFormSubmission = (inquiryType) => {
  trackEvent('contact_form_submitted', {
    event_category: 'engagement',
    inquiry_type: inquiryType || 'general'
  });
};

/**
 * Track recruitment service request
 */
export const trackRecruitmentRequest = () => {
  trackEvent('recruitment_request_submitted', {
    event_category: 'services'
  });
};

/**
 * Track training service request
 */
export const trackTrainingRequest = () => {
  trackEvent('training_request_submitted', {
    event_category: 'services'
  });
};

/**
 * Track course schedule request
 */
export const trackCourseScheduleRequest = (courseId, courseTitle) => {
  trackEvent('course_schedule_requested', {
    event_category: 'training',
    course_id: courseId,
    course_title: courseTitle
  });
};

/**
 * Track job view
 */
export const trackJobView = (jobId, jobTitle) => {
  trackEvent('job_viewed', {
    event_category: 'recruitment',
    job_id: jobId,
    job_title: jobTitle
  });
};

/**
 * Track course view
 */
export const trackCourseView = (courseId, courseTitle) => {
  trackEvent('course_viewed', {
    event_category: 'training',
    course_id: courseId,
    course_title: courseTitle
  });
};

/**
 * Track language change
 */
export const trackLanguageChange = (language) => {
  trackEvent('language_changed', {
    event_category: 'engagement',
    language: language
  });
};

export default {
  initGA,
  trackPageView,
  trackEvent,
  trackJobApplication,
  trackTalentPoolSignup,
  trackTrainingEnrollment,
  trackContactFormSubmission,
  trackRecruitmentRequest,
  trackTrainingRequest,
  trackCourseScheduleRequest,
  trackJobView,
  trackCourseView,
  trackLanguageChange
};
